import axios from 'axios';
import { errorLog, getLoggedInUser, getRefreshToken } from '@commonservice';
import { UNAUTHORIZED, EXPIRED_TOKEN, INVALID_CSRF, SUCCESS_CODE, BAD_REQUEST_ERROR_STATUS, NETWORK_ERROR_STATUS } from '@constants';
import { BASE_URL } from '@baseurl';

// Create Axios instance
const api = axios.create({
    baseURL: BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request Interceptor: Attach Bearer token to headers
api.interceptors.request.use(
    async (config) => {
        const token = await getLoggedInUser();
        if (token && config.headers) {
            config.headers['Authorization'] = `Bearer ${token.access_token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (!error.response) {
            // Network or timeout error → keep Axios error intact
            return Promise.reject(error);  // ✅ don't overwrite with new Error('')
        }
        return Promise.reject(error);
    },
);

const userLogout = () => {
    localStorage.removeItem('userDetails');
    const userId = {
        user_id: 0,
        user_name: 'expired',
    };
    localStorage.setItem('userDetails', JSON.stringify(userId));
    localStorage.setItem('isAuthenticated', false);

    // Redirect to login page via window.location
    window.location.href = '/#/session/signin';
};

// Unified API call function
export const callApi = async ({ endpoint, method = 'post', data, module, functionality }) => {
    try {
        const response = await api.request({
            url: endpoint,
            method,
            data,
        });
        const resData = response?.data;
        if (resData?.successcode) {
            if (resData.successcode === UNAUTHORIZED) {
                const refresh = await getRefreshToken();
                if (refresh === EXPIRED_TOKEN) {
                    await userLogout();
                    return;
                }
                else {
                    return await callApi({ endpoint, method, data, module, functionality });
                }
            }

            if (resData.successcode === INVALID_CSRF) {
                await userLogout();
                return;
            }
            if (resData.successcode === BAD_REQUEST_ERROR_STATUS) {
                return;
            }

            if (resData.status === SUCCESS_CODE) {
                return resData;
            } else {
                throw new Error(resData.message);
            }
        } else {
            return '';
        }
    } catch (error) {
        const errMessage = error?.message || 'Unknown Error';
        await errorLog({
            error: errMessage,
            module: module || '',
            functionality: functionality || '',
        });
        if (error.code === NETWORK_ERROR_STATUS || error.message === 'Network Error') {
            return { 'successcode': 201, 'message': 'Server not reachable' };
        }
        return { 'successcode': 201, 'message': 'Something went wrong' };
    }
};
